import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { PreencherTesteButton } from "@/components/PreencherTesteButton";
import { gerarCartelaFake } from "@/lib/mock-test-data";
import { useIsEmpresaTestMode } from "@/hooks/use-modo-teste";
import { LerQrCartelaButton } from "@/components/laudos/LerQrCartelaButton";
import type { TipoVeiculoLaudo } from "@shared/etiqueta-qrcode";

interface AdicionarCartelaProps {
  cartelaAtual: string | null;
  laudoId: string;
  onSuccess: () => void;
  disabled?: boolean;
  cartelaConfirmada?: boolean;
  vistoriadoraId?: string;
  parceiroId?: string;
  tipoVeiculo?: TipoVeiculoLaudo | null;
}

export function AdicionarCartela({
  cartelaAtual,
  laudoId,
  onSuccess,
  disabled = false,
  cartelaConfirmada = false,
  vistoriadoraId,
  parceiroId,
  tipoVeiculo,
}: AdicionarCartelaProps) {
  const [open, setOpen] = useState(false);
  const [cartela, setCartela] = useState(cartelaAtual || "");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { session } = useAuth();
  const isTestModeV = useIsEmpresaTestMode(vistoriadoraId);
  const isTestModeP = useIsEmpresaTestMode(parceiroId);
  const isTestMode = isTestModeV || isTestModeP;

  const handleSave = async () => {
    // Validação
    if (!cartela.trim()) {
      toast({
        variant: "destructive",
        title: "Cartela obrigatória",
        description: "Por favor, preencha o número da cartela",
      });
      return;
    }

    if (cartela.replace(/\D/g, "").length !== 10) {
      toast({
        variant: "destructive",
        title: "Cartela inválida",
        description: "A cartela deve conter 10 dígitos",
      });
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(`/api/laudos/${laudoId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ cartela: cartela.trim() }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Erro ao salvar cartela");
      }

      toast({ title: "Cartela adicionada com sucesso!" });
      setOpen(false);
      onSuccess();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description:
          error instanceof Error ? error.message : "Erro ao salvar cartela",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled || cartelaConfirmada}
          className="gap-2"
          title={
            cartelaConfirmada
              ? "Cartela confirmada. Apenas OWNER pode alterar."
              : ""
          }
        >
          <Plus className="w-4 h-4" />
          {cartelaAtual ? "Editar Cartela" : "Adicionar Cartela"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {cartelaAtual ? "Editar Cartela" : "Adicionar Cartela"}
          </DialogTitle>
          <DialogDescription>
            Digite o número da cartela (10 dígitos)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {cartelaAtual && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Cartela atual: <strong>{cartelaAtual}</strong>
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="cartela">Número da Cartela</Label>
              {isTestMode && (
                <PreencherTesteButton
                  onFill={() => setCartela(gerarCartelaFake())}
                />
              )}
            </div>
            <div className="flex gap-2">
              <Input
                id="cartela"
                className="min-w-0"
                placeholder="0000000000"
                value={cartela}
                onChange={(e) => setCartela(e.target.value)}
                maxLength={10}
                pattern="(?:0|[1-9]\d*)"
                disabled={isLoading}
                inputMode="numeric"
                type="numeric"
                autoFocus
              />
              <LerQrCartelaButton tipoVeiculo={tipoVeiculo} onCartela={setCartela} disabled={isLoading} />
            </div>
            <p className="text-xs text-slate-500">
              {cartela.replace(/\D/g, "").length}/10 dígitos
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={isLoading}>
              {isLoading ? "Salvando..." : "Salvar Cartela"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
